import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';

import { useSearchParams } from 'react-router-dom';

import { toast } from 'react-toastify';

import { PageCard } from '../../components/layout/page/PageCard';

import { PageHeader } from '../../components/layout/page/PageHeader';

import { PageShell } from '../../components/layout/page/PageShell';

import { SettingsTabBar } from '../../components/settings/SettingsTabBar';

import { SettingsTabItem } from '../../components/settings/SettingsTabItem';

import { PlanningCycleFormModal } from '../../components/strategic-planning/PlanningCycleFormModal';

import { PlanningDeleteConfirmModal } from '../../components/strategic-planning/PlanningDeleteConfirmModal';

import { PlanningDuplicateModal } from '../../components/strategic-planning/PlanningDuplicateModal';

import { PlanningExtendDeadlineModal } from '../../components/strategic-planning/PlanningExtendDeadlineModal';

import { PlanningFiltersBar } from '../../components/strategic-planning/PlanningFiltersBar';

import { PlanningObjectivesListView } from '../../components/strategic-planning/PlanningObjectivesListView';

import { PlanningStatusBadge } from '../../components/strategic-planning/PlanningStatusBadge';

import { PlanningTaskDetailPanel } from '../../components/strategic-planning/PlanningTaskDetailPanel';

import { PlanningTaskFormModal } from '../../components/strategic-planning/PlanningTaskFormModal';

import { PlanningSprintFormModal } from '../../components/strategic-planning/PlanningSprintFormModal';

import { PlanningSprintPicker } from '../../components/strategic-planning/PlanningSprintPicker';

import { PlanningTaskKanbanBoard } from '../../components/strategic-planning/PlanningTaskKanbanBoard';

import { PlanningWorkItemsListView } from '../../components/strategic-planning/PlanningWorkItemsListView';

import { DeleteIconButton } from '../../components/ui/DeleteIconButton';

import { DuplicateIconButton } from '../../components/ui/DuplicateIconButton';

import { EditIconButton } from '../../components/ui/EditIconButton';

import { IconActionButton } from '../../components/ui/IconActionButton';

import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

import { InlineAlert } from '../../components/ui/InlineAlert';

import { TableActionButton } from '../../components/ui/TableActionButton';

import { usePermissions } from '../../contexts/PermissionsContext';

import { useMessageBox } from '../../contexts/MessageBoxContext';

import {

  addPlanningTaskEvidence,

  deletePlanningTaskEvidence,

  updatePlanningTaskEvidence,

  cancelPlanningTask,

  createPlanningCycle,

  createPlanningSprint,

  createPlanningTask,

  deletePlanningCycle,

  deletePlanningSprint,

  downloadPlanningImportTemplate,

  duplicatePlanningCycle,

  duplicatePlanningTask,

  exportStrategicPlanningXlsx,

  extendPlanningWorkItemDeadline,

  finishPlanningTaskExecution,

  getCycleDeleteImpact,

  getPlanningTask,

  getPlanningWorkItem,

  assignPlanningTask,

  replacePlanningWorkItemAreas,

  getStrategicPlanningImportStatus,

  importStrategicPlanning,

  StrategicPlanningImportScope,

  listPlanningCycles,

  listPlanningObjectives,

  listPlanningSprints,

  listPlanningTasks,

  listPlanningWorkItems,

  PlanningCycle,

  PlanningCycleUpsertPayload,

  PlanningDeleteImpact,

  PlanningObjective,

  PlanningSprint,

  PlanningSprintUpsertPayload,

  PlanningWorkItem,

  PlanningWorkItemEvidenceFormPayload,

  PlanningWorkItemUpsertPayload,

  startPlanningTaskExecution,

  updatePlanningCycle,

  updatePlanningSprint,

  updatePlanningTask,

} from '../../services/strategicPlanningApi';

import { PlanningDuplicateCascadeOptions } from '../../types/PlanningDuplicateScope';

import { CompanyAreaId, DEFAULT_COMPANY_AREA, isCompanyAreaId } from '../../types/CompanyArea';

import { pickDefaultSprintId, PLANNING_BACKLOG_ID } from '../../types/PlanningSprint';

import { formatPlanningCycleKind, canEditPlanningTaskOwner, formatPlanningTaskPriority } from '../../types/PlanningTaskStatus';

import { formatDateBr } from '../../utils/formatDateBr';

import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

import { sortPlanningObjectives, sortPlanningWorkItems } from '../../utils/planningListSort';

import { StrategicPlanningTabId } from './StrategicPlanningTabId';

const TABS: SettingsTabItem<StrategicPlanningTabId>[] = [

  { id: 'cycles', label: 'Ciclos' },

  { id: 'objectives', label: 'Objetivos' },

  { id: 'metas', label: 'Metas' },

  { id: 'marcos', label: 'Marcos' },

  { id: 'tasks', label: 'Tarefas' },

];

const TASK_FILTER_TOOLTIPS = {
  cycle:
    'Restringe as tarefas ao ciclo de planejamento selecionado. Combine com sprint/backlog para refinar ainda mais.',
  objective: 'Restringe as tarefas ao objetivo estratégico selecionado, independentemente de meta ou marco.',
  meta: 'Restringe às tarefas da meta selecionada, incluindo as vinculadas aos marcos dela.',
  marco: 'Restringe às tarefas vinculadas ao marco selecionado. As opções respeitam ciclo, objetivo e meta.',
};

type TaskViewMode = 'list' | 'kanban';

const AREA_STORAGE_KEY = 'planning.selectedCompanyArea';

function readStoredCompanyArea(): CompanyAreaId {
  try {
    const stored = localStorage.getItem(AREA_STORAGE_KEY);
    if (stored && isCompanyAreaId(stored)) {
      return stored;
    }
  } catch {
    // ignore storage access errors
  }

  return DEFAULT_COMPANY_AREA;
}

function persistCompanyArea(area: CompanyAreaId): void {
  try {
    localStorage.setItem(AREA_STORAGE_KEY, area);
  } catch {
    // ignore storage access errors
  }
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function parseStrategicPlanningTab(value: string | null): StrategicPlanningTabId | null {
  if (
    value === 'cycles'
    || value === 'objectives'
    || value === 'metas'
    || value === 'marcos'
    || value === 'tasks'
  ) {
    return value;
  }

  return null;
}

function resolveInitialTab(searchParams: URLSearchParams): StrategicPlanningTabId {
  const fromTab = parseStrategicPlanningTab(searchParams.get('tab'));
  if (fromTab) {
    return fromTab;
  }

  return searchParams.get('taskId') ? 'tasks' : 'cycles';
}

export function StrategicPlanningPage() {

  const { canWriteModule, access } = usePermissions();

  const { confirmDelete } = useMessageBox();

  const canWrite = canWriteModule('strategic-planning');

  const currentUserId = access?.userId;

  const [searchParams] = useSearchParams();

  const importInputRef = useRef<HTMLInputElement | null>(null);

  const lastDeepLinkTaskIdRef = useRef<string | null>(null);

  const pendingSprintPreferenceRef = useRef<string | null>(null);

  const [importing, setImporting] = useState(false);

  /** Força remount das listas filhas após importação concluída. */
  const [importRefreshKey, setImportRefreshKey] = useState(0);

  const [activeTab, setActiveTab] = useState<StrategicPlanningTabId>(() =>
    resolveInitialTab(searchParams),
  );

  const [loading, setLoading] = useState(true);

  const [cycles, setCycles] = useState<PlanningCycle[]>([]);

  const [tasks, setTasks] = useState<PlanningWorkItem[]>([]);

  const [selectedCycleFilter, setSelectedCycleFilter] = useState('');

  const [selectedObjectiveKindFilter, setSelectedObjectiveKindFilter] = useState('');

  const [selectedObjectiveFilter, setSelectedObjectiveFilter] = useState('');

  const [selectedMetaFilter, setSelectedMetaFilter] = useState('');

  const [selectedMarcoFilter, setSelectedMarcoFilter] = useState('');

  const [taskViewMode, setTaskViewMode] = useState<TaskViewMode>('kanban');

  const [selectedArea, setSelectedArea] = useState<CompanyAreaId>(readStoredCompanyArea);

  const [sprints, setSprints] = useState<PlanningSprint[]>([]);

  const [selectedSprintId, setSelectedSprintId] = useState(PLANNING_BACKLOG_ID);

  const [sprintModalOpen, setSprintModalOpen] = useState(false);

  const [editingSprint, setEditingSprint] = useState<PlanningSprint | null>(null);

  const [objectiveCreateOpen, setObjectiveCreateOpen] = useState(false);

  const [metaCreateOpen, setMetaCreateOpen] = useState(false);

  const [marcoCreateOpen, setMarcoCreateOpen] = useState(false);

  const [selectedTask, setSelectedTask] = useState<PlanningWorkItem | null>(null);

  const taskDetailReturnRef = useRef<PlanningWorkItem | null>(null);

  const [cycleModalOpen, setCycleModalOpen] = useState(false);

  const [editingCycle, setEditingCycle] = useState<PlanningCycle | null>(null);

  const [cycleDeleteContext, setCycleDeleteContext] = useState<{

    cycle: PlanningCycle;

    impact: PlanningDeleteImpact;

    transferOptions: Array<{ id: string; label: string }>;

  } | null>(null);

  const [cycleDuplicateContext, setCycleDuplicateContext] = useState<{

    cycle: PlanningCycle;

    counts: { objectives: number; metas: number; marcos: number; tasks: number };

  } | null>(null);

  const [taskModalOpen, setTaskModalOpen] = useState(false);

  const [editingTask, setEditingTask] = useState<PlanningWorkItem | null>(null);

  const [extendDeadlineTask, setExtendDeadlineTask] = useState<PlanningWorkItem | null>(null);
  const [taskObjectiveOptions, setTaskObjectiveOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [taskMetaOptions, setTaskMetaOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [taskMarcoOptions, setTaskMarcoOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [taskParentLabels, setTaskParentLabels] = useState<Array<{ id: string; label: string }>>([]);
  const [taskSortObjectives, setTaskSortObjectives] = useState<PlanningObjective[]>([]);
  const [taskSortParents, setTaskSortParents] = useState<PlanningWorkItem[]>([]);

  const filteredTasks = useMemo(() => {

    let items = tasks.filter((task) => {
      const areas = task.areas ?? [];
      return areas.length === 0 || areas.includes(selectedArea);
    });

    if (selectedCycleFilter) {

      items = items.filter((task) => task.cycleId === selectedCycleFilter);

    }

    if (selectedObjectiveFilter) {

      items = items.filter((task) => task.objectiveId === selectedObjectiveFilter);

    }

    if (selectedMetaFilter) {
      const marcoIdsUnderMeta = taskSortParents
        .filter((item) => item.parentWorkItemId === selectedMetaFilter)
        .map((item) => item.id);

      items = items.filter(
        (task) =>
          task.parentWorkItemId === selectedMetaFilter
          || (task.parentWorkItemId != null && marcoIdsUnderMeta.includes(task.parentWorkItemId)),
      );
    }

    if (selectedMarcoFilter) {
      items = items.filter((task) => task.parentWorkItemId === selectedMarcoFilter);
    }

    return sortPlanningWorkItems(items, {
      cycles,
      objectives: taskSortObjectives,
      parents: taskSortParents,
    });

  }, [tasks, selectedArea, selectedCycleFilter, selectedMarcoFilter, selectedMetaFilter, selectedObjectiveFilter, taskSortObjectives, taskSortParents, cycles]);

  function resolveTaskLinkLabel(task: PlanningWorkItem): string {
    if (task.parentWorkItemId) {
      return taskParentLabels.find((item) => item.id === task.parentWorkItemId)?.label ?? 'Meta/marco';
    }

    if (task.objectiveId) {
      const objectiveTitle = taskObjectiveOptions.find((item) => item.id === task.objectiveId)?.label;
      return objectiveTitle ? `Objetivo: ${objectiveTitle}` : 'Somente objetivo';
    }

    return 'Sem vínculo';
  }

  const cyclesLoadedRef = useRef(false);

  const loadCycles = useCallback(async (force = false): Promise<void> => {
    if (cyclesLoadedRef.current && !force) {
      return;
    }

    setCycles(await listPlanningCycles());
    cyclesLoadedRef.current = true;
  }, []);

  const loadSprints = useCallback(async (preferSprintId?: string, area = selectedArea): Promise<void> => {

    const items = await listPlanningSprints(area);

    setSprints(items);

    const preferred = preferSprintId ?? pendingSprintPreferenceRef.current ?? undefined;
    if (preferred != null) {
      pendingSprintPreferenceRef.current = null;
    }

    setSelectedSprintId((current) => {

      if (preferred === PLANNING_BACKLOG_ID) {
        return PLANNING_BACKLOG_ID;
      }

      if (preferred && items.some((sprint) => sprint.id === preferred)) {

        return preferred;

      }

      if (current !== PLANNING_BACKLOG_ID && items.some((sprint) => sprint.id === current)) {

        return current;

      }

      return pickDefaultSprintId(items);

    });

  }, [selectedArea]);

  const loadTasks = useCallback(async (): Promise<void> => {
    // Ciclo/objetivo/meta/marco filtrados no cliente (filteredTasks) — só sprint/backlog no servidor.
    const items = await listPlanningTasks({
      ...(selectedSprintId === PLANNING_BACKLOG_ID
        ? { backlogOnly: true }
        : { sprintId: selectedSprintId }),
    });

    setTasks(items);
  }, [selectedSprintId]);

  const loadActiveTab = useCallback(async (): Promise<void> => {
    // Só bloqueia a tela no primeiro carregamento de ciclos; troca de aba reaproveita cache.
    const needsBlockingLoad = !cyclesLoadedRef.current;
    if (needsBlockingLoad) {
      setLoading(true);
    }

    try {
      if (activeTab === 'cycles') {
        await loadCycles(true);
      } else if (activeTab === 'tasks') {
        await Promise.all([loadCycles(false), loadSprints()]);
      } else {
        await loadCycles(false);
      }
    } catch {
      toast.error('Falha ao carregar planejamento estratégico.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadCycles, loadSprints]);

  useEffect(() => {
    void loadActiveTab();
  }, [loadActiveTab]);

  useEffect(() => {
    if (activeTab === 'tasks') {
      void loadTasks();
    }
  }, [activeTab, loadTasks]);

  useEffect(() => {
    // Labels de filtro da aba Tarefas — 3 chamadas em paralelo (objetivos + metas + marcos).
    if (activeTab !== 'tasks') {
      return;
    }

    let cancelled = false;

    async function loadTaskFilterLabels(): Promise<void> {
      try {
        const [objectives, metas, marcos] = await Promise.all([
          listPlanningObjectives(),
          listPlanningWorkItems({ kind: 'Meta' }),
          listPlanningWorkItems({ kind: 'Marco' }),
        ]);

        if (cancelled) return;

        const orderedObjectives = sortPlanningObjectives(objectives, cycles);
        const metaItems = metas.filter((item) => !item.parentWorkItemId);
        const orderedMetas = sortPlanningWorkItems(metaItems, {
          cycles,
          objectives: orderedObjectives,
          parents: [],
        });
        const orderedMarcos = sortPlanningWorkItems(marcos, {
          cycles,
          objectives: orderedObjectives,
          parents: metaItems,
        });

        setTaskSortObjectives(orderedObjectives);
        setTaskObjectiveOptions(orderedObjectives.map((item) => ({ id: item.id, label: item.title })));
        setTaskSortParents([...metaItems, ...orderedMarcos]);
        setTaskMetaOptions(orderedMetas.map((item) => ({ id: item.id, label: item.title })));
        setTaskParentLabels([
          ...orderedMetas.map((item) => ({ id: item.id, label: `Meta: ${item.title}` })),
          ...orderedMarcos.map((item) => ({
            id: item.id,
            label: `Marco: ${item.title}`,
          })),
        ]);
      } catch {
        if (cancelled) return;
        setTaskObjectiveOptions([]);
        setTaskMetaOptions([]);
        setTaskMarcoOptions([]);
        setTaskParentLabels([]);
        setTaskSortObjectives([]);
        setTaskSortParents([]);
      }
    }

    void loadTaskFilterLabels();
    return () => {
      cancelled = true;
    };
  }, [activeTab, cycles]);

  // Filtra opções de objetivo/meta/marco no cliente (sem refetch).
  useEffect(() => {
    if (activeTab !== 'tasks') {
      return;
    }

    const objectives = selectedCycleFilter
      ? taskSortObjectives.filter((item) => item.cycleId === selectedCycleFilter)
      : taskSortObjectives;
    setTaskObjectiveOptions(objectives.map((item) => ({ id: item.id, label: item.title })));

    let metas = taskSortParents.filter((item) => item.kind === 'Meta');
    if (selectedCycleFilter) {
      metas = metas.filter((item) => item.cycleId === selectedCycleFilter);
    }
    if (selectedObjectiveFilter) {
      metas = metas.filter((item) => item.objectiveId === selectedObjectiveFilter);
    }
    setTaskMetaOptions(metas.map((item) => ({ id: item.id, label: item.title })));

    let marcos = taskSortParents.filter((item) => item.kind === 'Marco');
    if (selectedCycleFilter) {
      marcos = marcos.filter((item) => item.cycleId === selectedCycleFilter);
    }
    if (selectedObjectiveFilter) {
      marcos = marcos.filter((item) => item.objectiveId === selectedObjectiveFilter);
    }
    if (selectedMetaFilter) {
      marcos = marcos.filter((item) => item.parentWorkItemId === selectedMetaFilter);
    }
    setTaskMarcoOptions(marcos.map((item) => ({ id: item.id, label: item.title })));
  }, [
    activeTab,
    selectedCycleFilter,
    selectedObjectiveFilter,
    selectedMetaFilter,
    taskSortObjectives,
    taskSortParents,
  ]);

  useEffect(() => {
    if (!selectedMarcoFilter) {
      return;
    }

    // Evita limpar o marco do deep link enquanto as opções ainda estão carregando.
    if (taskMarcoOptions.length === 0) {
      return;
    }

    if (!taskMarcoOptions.some((item) => item.id === selectedMarcoFilter)) {
      setSelectedMarcoFilter('');
    }
  }, [selectedMarcoFilter, taskMarcoOptions]);

  useEffect(() => {
    const taskId = searchParams.get('taskId')?.trim() || null;
    if (!taskId || lastDeepLinkTaskIdRef.current === taskId) {
      return;
    }

    lastDeepLinkTaskIdRef.current = taskId;
    const deepLinkTaskId = taskId;
    const tabFromUrl = parseStrategicPlanningTab(searchParams.get('tab')) ?? 'tasks';

    async function hydrateTaskFromUrl(): Promise<void> {
      try {
        const task = await getPlanningTask(deepLinkTaskId);
        let metaId = '';
        let marcoId = '';

        if (task.parentWorkItemId) {
          const parent = await getPlanningWorkItem(task.parentWorkItemId);
          if (parent.kind === 'Marco') {
            marcoId = parent.id;
            metaId = parent.parentWorkItemId ?? '';
          } else if (parent.kind === 'Meta') {
            metaId = parent.id;
          }
        }

        const sprintTarget = task.sprintId ?? PLANNING_BACKLOG_ID;
        pendingSprintPreferenceRef.current = sprintTarget;

        setSelectedCycleFilter(task.cycleId ?? '');
        setSelectedObjectiveFilter(task.objectiveId ?? '');
        setSelectedMetaFilter(metaId);
        setSelectedMarcoFilter(marcoId);
        setSelectedSprintId(sprintTarget);
        setActiveTab(tabFromUrl);
        setSelectedTask(task);

        await loadSprints(sprintTarget);
      } catch {
        toast.error('Não foi possível abrir a tarefa indicada no link.');
        setActiveTab(tabFromUrl);
      }
    }

    void hydrateTaskFromUrl();
  }, [searchParams, loadSprints]);

  function navigateToTab(
    tab: StrategicPlanningTabId,
    filters?: { cycleId?: string; objectiveId?: string; metaId?: string; marcoId?: string },
  ): void {
    if (filters?.cycleId) setSelectedCycleFilter(filters.cycleId);
    if (filters?.objectiveId !== undefined) setSelectedObjectiveFilter(filters.objectiveId);
    if (filters?.metaId !== undefined) setSelectedMetaFilter(filters.metaId);
    if (filters?.marcoId !== undefined) setSelectedMarcoFilter(filters.marcoId);
    setActiveTab(tab);
  }

  /** Sobe um nível na hierarquia (Tarefas→Marcos→Metas→Objetivos→Ciclos), preservando o pai selecionado. */
  function navigateBack(): void {
    const cycleId = selectedCycleFilter || undefined;
    const objectiveId = selectedObjectiveFilter || undefined;

    if (activeTab === 'tasks') {
      let metaId = selectedMetaFilter;

      if (selectedMarcoFilter) {
        const marco = taskSortParents.find((item) => item.id === selectedMarcoFilter);
        if (marco?.parentWorkItemId) {
          metaId = marco.parentWorkItemId;
        }
      } else if (metaId) {
        // Compat: atalho antigo gravava o id do marco em metaId.
        const maybeMarco = taskSortParents.find((item) => item.id === metaId && item.kind === 'Marco');
        if (maybeMarco?.parentWorkItemId) {
          setSelectedMarcoFilter(maybeMarco.id);
          metaId = maybeMarco.parentWorkItemId;
        }
      }

      navigateToTab('marcos', {
        cycleId,
        objectiveId,
        metaId: metaId || undefined,
      });
      return;
    }

    if (activeTab === 'marcos') {
      navigateToTab('metas', {
        cycleId,
        objectiveId: objectiveId ?? '',
      });
      return;
    }

    if (activeTab === 'metas') {
      navigateToTab('objectives', {
        cycleId: cycleId ?? '',
      });
      return;
    }

    if (activeTab === 'objectives') {
      setActiveTab('cycles');
    }
  }

  const canNavigateBack = activeTab !== 'cycles';

  async function refreshTask(taskId: string): Promise<PlanningWorkItem> {

    const fresh = await getPlanningTask(taskId);

    setTasks((current) => current.map((item) => (item.id === taskId ? fresh : item)));

    setSelectedTask(fresh);

    return fresh;

  }

  async function handleCycleSubmit(payload: PlanningCycleUpsertPayload): Promise<void> {

    try {

      if (editingCycle) {

        await updatePlanningCycle(editingCycle.id, payload);

        toast.success('Ciclo atualizado.');

      } else {

        await createPlanningCycle(payload);

        toast.success('Ciclo criado.');

      }

      setEditingCycle(null);

      await loadCycles(true);

    } catch {

      toast.error('Falha ao salvar ciclo.');

      throw new Error('cycle_save_failed');

    }

  }

  async function openCycleDelete(cycle: PlanningCycle): Promise<void> {

    try {

      const impact = await getCycleDeleteImpact(cycle.id);

      const transferOptions = cycles

        .filter((item) => item.id !== cycle.id)

        .map((item) => ({ id: item.id, label: item.name }));

      setCycleDeleteContext({ cycle, impact, transferOptions });

    } catch {

      toast.error('Não foi possível avaliar o impacto da exclusão.');

    }

  }

  async function openCycleDuplicate(cycle: PlanningCycle): Promise<void> {

    try {

      const objectives = await listPlanningObjectives(cycle.id);

      const workItems = await listPlanningWorkItems({ cycleId: cycle.id });

      setCycleDuplicateContext({

        cycle,

        counts: {

          objectives: objectives.length,

          metas: workItems.filter((item) => item.kind === 'Meta').length,

          marcos: workItems.filter((item) => item.kind === 'Marco').length,

          tasks: workItems.filter((item) => item.kind === 'Tarefa').length,

        },

      });

    } catch {

      toast.error('Não foi possível preparar a duplicação.');

    }

  }

  async function handleCycleDuplicateConfirm(options: PlanningDuplicateCascadeOptions): Promise<void> {

    if (!cycleDuplicateContext) return;

    await duplicatePlanningCycle(cycleDuplicateContext.cycle.id, {

      includeObjectives: options.includeObjectives,

      includeMetas: options.includeMetas,

      includeMarcos: options.includeMarcos,

      includeTasks: options.includeTasks,

    });

    toast.success('Ciclo duplicado.');

    setCycleDuplicateContext(null);

    await loadCycles(true);

  }

  async function handleCycleDeleteConfirm(transferChildrenToId?: string): Promise<void> {

    if (!cycleDeleteContext) return;

    try {

      await deletePlanningCycle(cycleDeleteContext.cycle.id, { transferChildrenToId });

      if (selectedCycleFilter === cycleDeleteContext.cycle.id) {

        setSelectedCycleFilter('');

      }

      toast.success('Ciclo excluído.');

      setCycleDeleteContext(null);

      await loadCycles(true);

    } catch {

      toast.error('Falha ao excluir ciclo.');

      throw new Error('cycle_delete_failed');

    }

  }

  async function handleTaskSubmit(payload: PlanningWorkItemUpsertPayload): Promise<void> {

    try {

      if (editingTask) {

        if (!payload.objectiveId && !payload.parentWorkItemId) {
          toast.error('Selecione o objetivo da tarefa.');
          throw new Error('task_parent_required');
        }

        await updatePlanningTask(editingTask.id, {
          ...payload,
          objectiveId: payload.objectiveId || undefined,
          parentWorkItemId: payload.parentWorkItemId || undefined,
        });

        toast.success('Tarefa atualizada.');

        await refreshTask(editingTask.id);

      } else {

        const createPayload: PlanningWorkItemUpsertPayload = {
          ...payload,
          objectiveId: payload.objectiveId || undefined,
          parentWorkItemId: payload.parentWorkItemId || undefined,
        };

        if (!createPayload.objectiveId && !createPayload.parentWorkItemId) {
          toast.error('Selecione o objetivo da tarefa. A meta e o marco são opcionais.');
          throw new Error('task_parent_required');
        }

        const created = await createPlanningTask(createPayload);

        toast.success('Tarefa criada.');

        setTasks((current) => [...current, created]);

        setSelectedTask(created);

      }

      setEditingTask(null);

      taskDetailReturnRef.current = null;

      if (activeTab === 'tasks') {

        await loadTasks();

      }

    } catch (error) {

      if (!(error instanceof Error && error.message === 'task_parent_required')) {
        toast.error(getRequestErrorMessage(error, 'Falha ao salvar tarefa.'));
      }

      throw error instanceof Error ? error : new Error('task_save_failed');

    }

  }

  function handleAreaChange(area: CompanyAreaId): void {
    persistCompanyArea(area);
    setSelectedArea(area);
  }

  async function handleSprintSubmit(payload: PlanningSprintUpsertPayload): Promise<void> {
    try {
      if (editingSprint) {
        const updated = await updatePlanningSprint(editingSprint.id, payload);
        toast.success('Sprint atualizada.');
        if (updated.area !== selectedArea && isCompanyAreaId(updated.area)) {
          persistCompanyArea(updated.area);
          setSelectedArea(updated.area);
          await loadSprints(updated.id, updated.area);
        } else {
          await loadSprints(updated.id);
        }
      } else {
        const created = await createPlanningSprint(payload);
        toast.success('Sprint criada.');
        if (created.area !== selectedArea && isCompanyAreaId(created.area)) {
          persistCompanyArea(created.area);
          setSelectedArea(created.area);
          await loadSprints(created.id, created.area);
        } else {
          await loadSprints(created.id);
        }
      }

      setEditingSprint(null);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar sprint.'));
      throw error instanceof Error ? error : new Error('sprint_save_failed');
    }
  }

  async function handleSprintDelete(sprint: PlanningSprint): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Excluir sprint',
        message: `Excluir a sprint "${sprint.title}"?`,
        details: ['As tarefas desta sprint voltam ao backlog.'],
      }))
    ) {
      return;
    }

    try {
      await deletePlanningSprint(sprint.id);
      toast.success('Sprint excluída. Tarefas foram para o backlog.');
      await loadSprints();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir sprint.'));
    }
  }

  async function handleSelectTask(task: PlanningWorkItem): Promise<void> {

    try {

      const fresh = await getPlanningTask(task.id);

      setSelectedTask(fresh);

    } catch {

      toast.error('Falha ao carregar tarefa.');

    }

  }

  async function handleStartExecution(taskId: string, executorUserId: string): Promise<void> {

    try {

      await startPlanningTaskExecution(taskId, { executorUserId });

      toast.success('Execução iniciada.');

      await refreshTask(taskId);

    } catch {

      toast.error('Não foi possível iniciar a execução.');

    }

  }

  async function handleAssignOwner(taskId: string, ownerUserId: string): Promise<void> {

    try {

      await assignPlanningTask(taskId, { ownerUserId });

      toast.success('Responsável atribuído.');

      await refreshTask(taskId);

    } catch (error) {

      toast.error(getRequestErrorMessage(error, 'Não foi possível atribuir o responsável.'));

      throw error instanceof Error ? error : new Error('task_assign_failed');

    }

  }

  async function handleReplaceTaskAreas(taskId: string, areas: string[]): Promise<void> {

    try {

      await replacePlanningWorkItemAreas(taskId, areas);

      toast.success('Áreas da tarefa atualizadas.');

      await refreshTask(taskId);

      await loadTasks();

    } catch (error) {

      toast.error(getRequestErrorMessage(error, 'Não foi possível atualizar as áreas da tarefa.'));

      throw error instanceof Error ? error : new Error('task_areas_failed');

    }

  }

  async function handleDuplicateTask(taskId: string): Promise<void> {

    try {

      await duplicatePlanningTask(taskId);

      toast.success('Tarefa duplicada sem responsável.');

      await loadTasks();

    } catch (error) {

      toast.error(getRequestErrorMessage(error, 'Não foi possível duplicar a tarefa.'));

      throw error instanceof Error ? error : new Error('task_duplicate_failed');

    }

  }

  async function handleFinishExecution(taskId: string, fulfillmentResult: string): Promise<void> {

    try {

      await finishPlanningTaskExecution(taskId, { fulfillmentResult });

      toast.success('Execução finalizada.');

      await refreshTask(taskId);

    } catch {

      toast.error('Não foi possível finalizar a execução.');

    }

  }

  async function handleCancelTask(taskId: string, cancellationReason: string): Promise<void> {

    try {

      await cancelPlanningTask(taskId, { cancellationReason });

      toast.success('Tarefa cancelada.');

      await refreshTask(taskId);

    } catch {

      toast.error('Não foi possível cancelar a tarefa.');

    }

  }

  async function handleExtendDeadline(
    taskId: string,
    payload: { newPlannedEndDate: string; reason: string },
  ): Promise<void> {
    const extension = await extendPlanningWorkItemDeadline(taskId, payload);
    toast.success('Prazo estendido.');
    setExtendDeadlineTask(null);
    setSelectedTask(extension);
    await loadTasks();
  }

  async function handleAddEvidence(
    taskId: string,
    payload: PlanningWorkItemEvidenceFormPayload,
  ): Promise<void> {
    try {
      await addPlanningTaskEvidence(taskId, payload);
      toast.success('Evidência adicionada.');
      await refreshTask(taskId);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível adicionar evidência.'));
      throw error;
    }
  }

  async function handleUpdateEvidence(
    taskId: string,
    evidenceId: string,
    payload: PlanningWorkItemEvidenceFormPayload,
  ): Promise<void> {
    try {
      await updatePlanningTaskEvidence(taskId, evidenceId, payload);
      toast.success('Evidência atualizada.');
      await refreshTask(taskId);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível atualizar a evidência.'));
      throw error;
    }
  }

  async function handleDeleteEvidence(taskId: string, evidenceId: string): Promise<void> {
    if (!(await confirmDelete({
      title: 'Excluir evidência',
      message: 'Excluir esta evidência?',
    }))) {
      return;
    }

    try {
      await deletePlanningTaskEvidence(taskId, evidenceId);
      toast.success('Evidência excluída.');
      await refreshTask(taskId);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível excluir a evidência.'));
    }
  }

  function resolveImportContext(): {
    scope: StrategicPlanningImportScope;
    parentId: string;
    parentTitle: string;
    childLabel: string;
    label: string;
    fileName: string;
  } | null {
    // Ciclo cadastrado manualmente → importa só objetivos desse ciclo.
    if (activeTab === 'cycles' || activeTab === 'objectives') {
      if (!selectedCycleFilter) return null;
      const parentTitle =
        cycles.find((cycle) => cycle.id === selectedCycleFilter)?.name ?? 'Ciclo selecionado';
      return {
        scope: 'Ciclo',
        parentId: selectedCycleFilter,
        parentTitle,
        childLabel: 'objetivos',
        label: `objetivos do ciclo "${parentTitle}"`,
        fileName: `template-objetivos.csv`,
      };
    }
    // Objetivo selecionado → importa só metas.
    if (activeTab === 'metas') {
      if (!selectedObjectiveFilter) return null;
      const parentTitle =
        taskObjectiveOptions.find((item) => item.id === selectedObjectiveFilter)?.label
        ?? 'Objetivo selecionado';
      return {
        scope: 'Objetivo',
        parentId: selectedObjectiveFilter,
        parentTitle,
        childLabel: 'metas',
        label: `metas do objetivo "${parentTitle}"`,
        fileName: `template-metas.csv`,
      };
    }
    // Meta selecionada → importa só marcos.
    if (activeTab === 'marcos') {
      if (!selectedMetaFilter) return null;
      const parentTitle =
        taskMetaOptions.find((item) => item.id === selectedMetaFilter)?.label ?? 'Meta selecionada';
      return {
        scope: 'Meta',
        parentId: selectedMetaFilter,
        parentTitle,
        childLabel: 'marcos',
        label: `marcos da meta "${parentTitle}"`,
        fileName: `template-marcos.csv`,
      };
    }
    // Marco selecionado → importa tarefas desse marco. Sem marco, a meta recebe as tarefas.
    if (activeTab === 'tasks') {
      if (selectedMarcoFilter) {
        const parentTitle =
          taskMarcoOptions.find((item) => item.id === selectedMarcoFilter)?.label
          ?? 'Marco selecionado';
        return {
          scope: 'Marco',
          parentId: selectedMarcoFilter,
          parentTitle,
          childLabel: 'tarefas',
          label: `tarefas do marco "${parentTitle}"`,
          fileName: `template-tarefas.csv`,
        };
      }
      if (!selectedMetaFilter) return null;
      const parentTitle =
        taskMetaOptions.find((item) => item.id === selectedMetaFilter)?.label ?? 'Meta selecionada';
      return {
        scope: 'MetaTarefas',
        parentId: selectedMetaFilter,
        parentTitle,
        childLabel: 'tarefas',
        label: `tarefas da meta "${parentTitle}"`,
        fileName: `template-tarefas.csv`,
      };
    }
    return null;
  }

  async function handleDownloadTemplate(): Promise<void> {
    const context = resolveImportContext();
    if (!context) {
      toast.info(
        activeTab === 'cycles' || activeTab === 'objectives'
          ? 'Selecione o ciclo (planejamento) para baixar o template de objetivos.'
          : activeTab === 'metas'
            ? 'Selecione o objetivo para baixar o template de metas.'
            : activeTab === 'marcos'
              ? 'Selecione a meta para baixar o template.'
              : activeTab === 'tasks'
                ? 'Selecione a meta ou o marco para baixar o template.'
                : 'Selecione o item pai nos filtros da aba.',
      );
      return;
    }
    try {
      const blob = await downloadPlanningImportTemplate(context.scope, context.parentId);
      triggerBlobDownload(blob, context.fileName);
      toast.success(
        context.scope === 'Marco' || context.scope === 'MetaTarefas'
          ? `Template de ${context.label} baixado. O marco entra pelo nome na coluna PaiTitulo — não é preciso informar o identificador.`
          : `Template de ${context.label} baixado. Colunas PaiTipo, PaiTitulo e PaiId já vêm preenchidas.`,
      );
    } catch {
      toast.error('Falha ao baixar template.');
    }
  }

  async function pollImportStatus(importId: string): Promise<void> {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const status = await getStrategicPlanningImportStatus(importId);
        if (status.status === 'Completed') {
          toast.success(
            `Importação concluída: ${status.createdCount} criados, ${status.failedCount} falhas.`,
          );
          if (status.errorSummary) {
            toast.info(status.errorSummary);
          }
          const nextCycles = await listPlanningCycles();
          setCycles(nextCycles);
          setImportRefreshKey((current) => current + 1);
          if (activeTab === 'tasks') {
            const nextTasks = await listPlanningTasks({
              ...(selectedCycleFilter ? { cycleId: selectedCycleFilter } : {}),
              ...(selectedObjectiveFilter ? { objectiveId: selectedObjectiveFilter } : {}),
              ...(selectedMarcoFilter ? { parentWorkItemId: selectedMarcoFilter } : {}),
            });
            setTasks(nextTasks);
          }
          return;
        }
        if (status.status === 'Failed') {
          toast.error(status.errorSummary || 'Importação falhou.');
          return;
        }
      } catch {
        // Continua tentando enquanto o job processa.
      }
    }
    toast.info('Importação ainda em andamento. Atualize a página quando concluir.');
  }

  async function handleImportFile(file: File | undefined): Promise<void> {
    if (!file || !canWrite) return;
    const context = resolveImportContext();
    if (!context) {
      toast.info(
        activeTab === 'tasks'
          ? 'Selecione a meta ou o marco nos filtros antes de importar.'
          : 'Selecione o item pai nos filtros (ciclo, objetivo ou meta) antes de importar.',
      );
      return;
    }
    setImporting(true);
    try {
      const queued = await importStrategicPlanning(file, context.scope, context.parentId);
      toast.success(
        queued.hangfireJobId
          ? `Importação de ${context.label} enfileirada.`
          : `Importação de ${context.label} enfileirada.`,
      );
      void pollImportStatus(queued.importId);
    } catch (error) {
      const detail =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      toast.error(detail || 'Falha ao enviar arquivo de importação.');
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  }

  async function handleExportXlsx(): Promise<void> {
    try {
      const blob = await exportStrategicPlanningXlsx(
        selectedCycleFilter || undefined,
      );
      triggerBlobDownload(
        blob,
        `planejamento-estrategico-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
      toast.success('Exportação concluída.');
    } catch {
      toast.error('Falha ao exportar planejamento.');
    }
  }

  const createAction =

    canWrite && activeTab === 'cycles' ? (

      <button

        type="button"

        className="erp-btn-primary"

        onClick={() => {

          setEditingCycle(null);

          setCycleModalOpen(true);

        }}

      >

        Novo ciclo

      </button>

    ) : canWrite && activeTab === 'objectives' ? (

      <button

        type="button"

        className="erp-btn-primary"

        onClick={() => setObjectiveCreateOpen(true)}

        disabled={cycles.length === 0}

      >

        Novo objetivo

      </button>

    ) : canWrite && activeTab === 'metas' ? (

      <button

        type="button"

        className="erp-btn-primary"

        onClick={() => setMetaCreateOpen(true)}

        disabled={cycles.length === 0}

      >

        Nova meta

      </button>

    ) : canWrite && activeTab === 'marcos' ? (

      <button

        type="button"

        className="erp-btn-primary"

        onClick={() => setMarcoCreateOpen(true)}

        disabled={cycles.length === 0}

      >

        Novo marco

      </button>

    ) : canWrite && activeTab === 'tasks' ? (

      <button

        type="button"

        className="erp-btn-primary"

        onClick={() => {

          taskDetailReturnRef.current = null;

          setEditingTask(null);

          setTaskModalOpen(true);

        }}

      >

        Nova tarefa

      </button>

    ) : null;

  const importContext = resolveImportContext();
  const canImportHere = Boolean(importContext);

  const headerAction = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="erp-btn-secondary"
        disabled={!canImportHere}
        title={
          canImportHere
            ? `Baixar template de ${importContext?.childLabel} para ${importContext?.parentTitle}`
            : activeTab === 'tasks'
              ? 'Selecione a meta ou o marco nos filtros'
              : 'Selecione o item pai nos filtros (ciclo, objetivo ou meta)'
        }
        onClick={() => void handleDownloadTemplate()}
      >
        {canImportHere ? `Template ${importContext?.childLabel}` : 'Template'}
      </button>
      {canWrite ? (
        <>
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(event) => void handleImportFile(event.target.files?.[0])}
          />
          <button
            type="button"
            className="erp-btn-secondary"
            disabled={importing || !canImportHere}
            title={
              canImportHere
                ? `Importar ${importContext?.childLabel} em ${importContext?.parentTitle}`
                : activeTab === 'tasks'
                  ? 'Selecione a meta ou o marco nos filtros'
                  : 'Selecione o item pai nos filtros (ciclo, objetivo ou meta)'
            }
            onClick={() => importInputRef.current?.click()}
          >
            {importing
              ? 'Importando...'
              : canImportHere
                ? `Importar ${importContext?.childLabel}`
                : 'Importar'}
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="erp-btn-secondary"
        onClick={() => void handleExportXlsx()}
      >
        Exportar
      </button>
      {createAction}
    </div>
  );

  return (

    <PageShell>

      <PageHeader

        title="Planejamento estratégico"

        description="Transforme a estratégia em ação. Organize ciclos de planejamento, defina objetivos e metas e acompanhe marcos e tarefas para alcançar os resultados do negócio."

        actions={headerAction}

      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SettingsTabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
        <button
          type="button"
          className="erp-btn-secondary inline-flex shrink-0 items-center gap-1.5"
          disabled={!canNavigateBack}
          onClick={navigateBack}
          title="Voltar para a aba anterior com o item pai selecionado"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
          Voltar
        </button>
      </div>

      {loading ? (

        <div className="flex justify-center py-16">

          <LoadingSpinner />

        </div>

      ) : activeTab === 'cycles' ? (
        <>
        <PlanningFiltersBar
          cycles={cycles}
          selectedCycleFilter={selectedCycleFilter}
          onCycleFilterChange={setSelectedCycleFilter}
          counterLabel={
            selectedCycleFilter
              ? 'Ciclo selecionado — use Template/Importar objetivos no topo'
              : 'Selecione um ciclo para importar objetivos nele'
          }
        />

        <PageCard title="Ciclos de planejamento" subtitle={`${cycles.length} ciclo(s) cadastrado(s)`}>

          {cycles.length === 0 ? (

            <InlineAlert>
              Nenhum ciclo cadastrado. {canWrite ? 'Crie o primeiro ciclo para começar.' : ''}
            </InlineAlert>

          ) : (

            <div className="overflow-x-auto">

              <table className="erp-table w-full min-w-[900px]">

                <thead>

                  <tr>

                    <th>Nome</th>

                    <th>Tipo</th>

                    <th>Status</th>

                    <th>Período</th>

                    <th>Ações</th>

                  </tr>

                </thead>

                <tbody>

                  {cycles.map((cycle) => (

                    <tr
                      key={cycle.id}
                      className={
                        selectedCycleFilter === cycle.id
                          ? 'bg-primary-50/80'
                          : undefined
                      }
                    >

                      <td>

                        <div className="font-medium text-secondary-900">{cycle.name}</div>

                        {cycle.description ? (

                          <div className="text-xs text-secondary-500 line-clamp-1">{cycle.description}</div>

                        ) : null}

                      </td>

                      <td>{formatPlanningCycleKind(cycle.kind)}</td>

                      <td>

                        <PlanningStatusBadge scope="cycle" status={cycle.status} />

                      </td>

                      <td>

                        {formatDateBr(cycle.startDate)} — {formatDateBr(cycle.endDate)}

                      </td>

                      <td>

                        <div className="flex flex-wrap items-center gap-1">

                          <TableActionButton onClick={() => navigateToTab('objectives', { cycleId: cycle.id })}>

                            Objetivos

                          </TableActionButton>

                          {canWrite ? (

                            <>

                              <DuplicateIconButton

                                label="Duplicar ciclo"

                                onClick={() => void openCycleDuplicate(cycle)}

                              />

                              <EditIconButton

                                label="Editar ciclo"

                                onClick={() => {

                                  setEditingCycle(cycle);

                                  setCycleModalOpen(true);

                                }}

                              />

                              <DeleteIconButton

                                label="Excluir ciclo"

                                onClick={() => void openCycleDelete(cycle)}

                              />

                            </>

                          ) : null}

                        </div>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          )}

        </PageCard>
        </>

      ) : activeTab === 'objectives' ? (

        <PlanningObjectivesListView
          key={`objectives-${importRefreshKey}`}

          canWrite={canWrite}

          cycles={cycles}

          selectedCycleFilter={selectedCycleFilter}

          selectedObjectiveKindFilter={selectedObjectiveKindFilter}

          createModalOpen={objectiveCreateOpen}

          onCreateModalOpenChange={setObjectiveCreateOpen}

          onCycleFilterChange={setSelectedCycleFilter}

          onObjectiveKindFilterChange={setSelectedObjectiveKindFilter}

          onNavigateToTab={navigateToTab}

        />

      ) : activeTab === 'metas' ? (

        <PlanningWorkItemsListView
          key={`metas-${importRefreshKey}`}

          kind="Meta"

          canWrite={canWrite}

          cycles={cycles}

          selectedCycleFilter={selectedCycleFilter}

          selectedObjectiveFilter={selectedObjectiveFilter}

          selectedMetaFilter={selectedMetaFilter}

          createModalOpen={metaCreateOpen}

          onCreateModalOpenChange={setMetaCreateOpen}

          onCycleFilterChange={setSelectedCycleFilter}

          onObjectiveFilterChange={setSelectedObjectiveFilter}

          onMetaFilterChange={setSelectedMetaFilter}

          onNavigateToTab={navigateToTab}

        />

      ) : activeTab === 'marcos' ? (

        <PlanningWorkItemsListView
          key={`marcos-${importRefreshKey}`}

          kind="Marco"

          canWrite={canWrite}

          cycles={cycles}

          selectedCycleFilter={selectedCycleFilter}

          selectedObjectiveFilter={selectedObjectiveFilter}

          selectedMetaFilter={selectedMetaFilter}

          createModalOpen={marcoCreateOpen}

          onCreateModalOpenChange={setMarcoCreateOpen}

          onCycleFilterChange={setSelectedCycleFilter}

          onObjectiveFilterChange={setSelectedObjectiveFilter}

          onMetaFilterChange={setSelectedMetaFilter}

          onNavigateToTab={navigateToTab}

        />

      ) : (

        <div className="space-y-4">

          <PageCard>
            <PlanningSprintPicker
              area={selectedArea}
              sprints={sprints}
              selectedSprintId={selectedSprintId}
              canWrite={canWrite}
              onAreaChange={handleAreaChange}
              onSprintChange={setSelectedSprintId}
              onCreateSprint={() => {
                setEditingSprint(null);
                setSprintModalOpen(true);
              }}
              onEditSprint={(sprint) => {
                setEditingSprint(sprint);
                setSprintModalOpen(true);
              }}
              onDeleteSprint={(sprint) => void handleSprintDelete(sprint)}
            />
          </PageCard>

          <PlanningFiltersBar

            cycles={cycles}

            selectedCycleFilter={selectedCycleFilter}

            onCycleFilterChange={setSelectedCycleFilter}

            showObjectiveFilter

            selectedObjectiveFilter={selectedObjectiveFilter}

            onObjectiveFilterChange={setSelectedObjectiveFilter}

            objectiveOptions={taskObjectiveOptions}
            showMetaFilter
            selectedMetaFilter={selectedMetaFilter}
            onMetaFilterChange={(value) => {
              setSelectedMetaFilter(value);
              setSelectedMarcoFilter('');
            }}
            metaOptions={taskMetaOptions}
            showMarcoFilter
            selectedMarcoFilter={selectedMarcoFilter}
            onMarcoFilterChange={setSelectedMarcoFilter}
            marcoOptions={taskMarcoOptions}
            filterTooltips={TASK_FILTER_TOOLTIPS}

            counterLabel={`${filteredTasks.length} tarefa(s) · ${
              selectedSprintId === PLANNING_BACKLOG_ID
                ? 'backlog'
                : sprints.find((sprint) => sprint.id === selectedSprintId)?.title ?? 'sprint'
            }`}

          />

          <PageCard>

            <div className="mb-4 flex gap-2">

              <button

                type="button"

                className={`erp-btn-secondary ${taskViewMode === 'list' ? 'ring-2 ring-primary-500' : ''}`}

                onClick={() => setTaskViewMode('list')}

              >

                Lista

              </button>

              <button

                type="button"

                className={`erp-btn-secondary ${taskViewMode === 'kanban' ? 'ring-2 ring-primary-500' : ''}`}

                onClick={() => setTaskViewMode('kanban')}

              >

                Kanban

              </button>

            </div>

            {taskViewMode === 'kanban' ? (
              <PlanningTaskKanbanBoard
                tasks={filteredTasks}
                onSelect={(task) => void handleSelectTask(task)}
              />
            ) : (

              <div className="overflow-x-auto">

                <table className="erp-table w-full min-w-[900px]">

                  <thead>

                    <tr>

                      <th>Título</th>

                      <th>Vínculo</th>

                      <th>Status</th>

                      <th>Prioridade</th>

                      <th>Responsável</th>

                      <th>Prazo</th>

                      {canWrite ? <th>Ações</th> : null}

                    </tr>

                  </thead>

                  <tbody>

                    {filteredTasks.map((task) => (

                      <tr key={task.id}>

                        <td>{task.title}</td>

                        <td>{resolveTaskLinkLabel(task)}</td>

                        <td>

                          <PlanningStatusBadge scope="workItem" status={task.status} />

                        </td>

                        <td>{formatPlanningTaskPriority(task.priority)}</td>

                        <td>{task.ownerUserName?.trim() || 'Não atribuído'}</td>

                        <td>{formatDateBr(task.plannedEndDate)}</td>

                        <td>

                          <div className="flex items-center gap-1">

                            <IconActionButton
                              label="Ver detalhes"
                              icon={EyeIcon}
                              onClick={() => void handleSelectTask(task)}
                            />

                            {canWrite ? (
                              <DuplicateIconButton
                                label="Duplicar tarefa"
                                onClick={() => void handleDuplicateTask(task.id)}
                              />
                            ) : null}

                            {canWrite && canEditPlanningTaskOwner(task.status, task.executionFinishedAt) ? (

                              <EditIconButton

                                label="Editar tarefa"

                                onClick={() => {

                                  taskDetailReturnRef.current = null;

                                  setEditingTask(task);

                                  setTaskModalOpen(true);

                                }}

                              />

                            ) : null}

                          </div>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            )}

          </PageCard>

          <PlanningTaskDetailPanel
            task={selectedTask}
            canWrite={canWrite}
            currentUserId={currentUserId}
            linkLabel={selectedTask ? resolveTaskLinkLabel(selectedTask) : undefined}
            onClose={() => setSelectedTask(null)}
            onStartExecution={handleStartExecution}
            onFinishExecution={handleFinishExecution}
            onCancel={handleCancelTask}
            onAddEvidence={handleAddEvidence}
            onUpdateEvidence={handleUpdateEvidence}
            onDeleteEvidence={handleDeleteEvidence}
            onAssign={handleAssignOwner}
            onAreasChange={handleReplaceTaskAreas}
            onDuplicate={handleDuplicateTask}
            onEdit={(task) => {
              taskDetailReturnRef.current = task;
              setSelectedTask(null);
              setEditingTask(task);
              setTaskModalOpen(true);
            }}
            onExtendDeadline={(task) => setExtendDeadlineTask(task)}
            isExecutive={Boolean(access?.roleCodes?.some((code) => code.toLowerCase() === 'admin'))}
          />

        </div>

      )}

      <PlanningCycleFormModal

        open={cycleModalOpen}

        initial={editingCycle}

        existingCycles={cycles}

        onClose={() => {

          setCycleModalOpen(false);

          setEditingCycle(null);

        }}

        onSubmit={handleCycleSubmit}

      />

      {cycleDeleteContext ? (

        <PlanningDeleteConfirmModal

          open

          title="Excluir ciclo"

          itemLabel={cycleDeleteContext.cycle.name}

          impact={cycleDeleteContext.impact}

          transferOptions={cycleDeleteContext.transferOptions}

          transferLabel="Mover objetivos e itens para o ciclo"

          onClose={() => setCycleDeleteContext(null)}

          onConfirm={handleCycleDeleteConfirm}

        />

      ) : null}

      {cycleDuplicateContext ? (

        <PlanningDuplicateModal

          open

          entityType="cycle"

          itemLabel={cycleDuplicateContext.cycle.name}

          counts={cycleDuplicateContext.counts}

          onClose={() => setCycleDuplicateContext(null)}

          onConfirm={handleCycleDuplicateConfirm}

        />

      ) : null}

      <PlanningTaskFormModal

        open={taskModalOpen}

        cycles={cycles}

        sprints={sprints}

        initial={editingTask}

        defaultCycleId={selectedCycleFilter}

        defaultSprintId={selectedSprintId}

        defaultObjectiveId={selectedObjectiveFilter || undefined}

        defaultParentWorkItemId={selectedMarcoFilter || selectedMetaFilter || undefined}

        defaultPlannedStartDate={cycles.find((cycle) => cycle.id === selectedCycleFilter)?.startDate}

        defaultPlannedEndDate={cycles.find((cycle) => cycle.id === selectedCycleFilter)?.endDate}

        defaultArea={selectedArea}

        onClose={() => {

          setTaskModalOpen(false);

          setEditingTask(null);

          const returnTask = taskDetailReturnRef.current;

          taskDetailReturnRef.current = null;

          if (returnTask) {
            setSelectedTask(returnTask);
          }

        }}

        onSubmit={handleTaskSubmit}

      />

      <PlanningSprintFormModal
        open={sprintModalOpen}
        initial={editingSprint}
        defaultArea={selectedArea}
        existingSprints={sprints}
        onClose={() => {
          setSprintModalOpen(false);
          setEditingSprint(null);
        }}
        onSubmit={handleSprintSubmit}
      />

      {extendDeadlineTask ? (
        <PlanningExtendDeadlineModal
          open
          currentEndDate={extendDeadlineTask.plannedEndDate ?? undefined}
          onClose={() => setExtendDeadlineTask(null)}
          onSubmit={async (payload) => {
            try {
              await handleExtendDeadline(extendDeadlineTask.id, payload);
            } catch {
              toast.error('Falha ao estender prazo.');
              throw new Error('extend_deadline_failed');
            }
          }}
        />
      ) : null}

    </PageShell>

  );

}

